// Noise filter for a Sentry `beforeSend` hook. Keeps error monitoring
// signal-dense by dropping only known-noise classes; any unrecognized error
// stays visible — 500s, real 4xx, and every other error are kept.
// Deliberately import-free so a boot-critical Sentry init can consume it
// without pulling in an API layer — the API error class is duck-typed by
// NAME (an Error whose `name` includes 'FetchApiError', carrying
// `extra.status` / `extra.endpointId`).

const IGNORED_ERROR_NAMES = new Set([
  'AbortError',
  'ConnectorNotConnectedError',
]);

const IGNORED_ERROR_MESSAGE_FRAGMENTS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // @walletconnect/core relayer internals: the relay WebSocket closing while
  // a topic subscribe is in flight rejects an SDK-internal promise nobody
  // awaits (background socket churn on sleep/wake — the relayer resubscribes
  // on reconnect; user-facing relay failures surface through the app's own
  // awaited paths, never this rejection).
  'Connection interrupted while trying to subscribe',
];

function isNoiseMessage(message: string): boolean {
  if (IGNORED_ERROR_MESSAGE_FRAGMENTS.some((m) => message.includes(m))) {
    return true;
  }
  // Network phase never reached the backend (offline, deploy gap, blocked
  // request). Chrome says "Failed to fetch", Safari "Load failed", Firefox
  // "NetworkError when attempting to fetch resource".
  if (/Failed to fetch|Load failed|NetworkError/.test(message)) {
    return true;
  }
  // httpClientIntegration synthesizes these for non-ok plain-fetch responses
  // — the backend-redeploy gateway burst as seen without a thrown error.
  // 504 deliberately stays visible: it is not part of the redeploy burst
  // (verified live 2026-08-16: zero 504s in 90 days) and a real one must
  // surface as the tripwire that 504 handling is worth building.
  if (/HTTP Client Error with status code: 50[23]\b/.test(message)) {
    return true;
  }
  return false;
}

// Non-Error values (strings, undefined) never match the error rules.
export function isNoiseError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (IGNORED_ERROR_NAMES.has(error.name)) return true;
  if (isNoiseMessage(error.message)) return true;
  // includes() rather than equality: consumers may re-capture the error
  // under a prefixed name ("…: FetchApiError").
  if (error.name.includes('FetchApiError')) {
    const extra: unknown = 'extra' in error ? error.extra : undefined;
    const status =
      typeof extra === 'object' && extra !== null && 'status' in extra ?
        extra.status
      : undefined;
    // Backend-redeploy gateway bursts. 504 deliberately stays visible —
    // same tripwire rationale as the message rule above.
    if (status === 502 || status === 503) return true;
    // Expired session on boot: GET_ISSUER 401 is the signal that routes the
    // user to re-auth — lifecycle, not an error.
    const endpointId =
      typeof extra === 'object' && extra !== null && 'endpointId' in extra ?
        extra.endpointId
      : undefined;
    if (status === 401 && endpointId === 'GET_ISSUER') return true;
  }
  return false;
}

// True = drop the event. The event's OWN exception.values are scanned with
// the message rules in addition to originalException: httpClientIntegration
// captures its synthesized 5xx events via captureEvent() with NO hint, so an
// originalException-only predicate silently keeps exactly the gateway events
// the "HTTP Client Error" rule targets.
export function isNoiseEvent(
  event: { exception?: { values?: { value?: string }[] } },
  originalException: unknown,
): boolean {
  if (isNoiseError(originalException)) return true;
  // Stryker disable next-line ArrayDeclaration: equivalent — the fallback only engages when values is nullish, and an injected string element fails the typeof v.value === 'string' guard, so some() is false either way
  return (event.exception?.values ?? []).some(
    (v) => typeof v.value === 'string' && isNoiseMessage(v.value),
  );
}
